import { ADDON_PRODUCTS, PRODUCTS } from './config.js';
import { parseNickname, PRICE_VARIANTS } from './priceCatalog.js';
import {
  copierQuantitiesFromSubscriptions,
  dedicatedEnvLinesOf,
  isDedicatedEnv,
  lineItemProduct,
  subscriptionIncludesProduct
} from './subscriptionLines.js';
import { listSeatEntitledSubscriptions } from './subscriptionStatuses.js';

/**
 * What the seat-entitled (active/trialing) subscriptions hold, as the billing page's
 * "Current plan" card lists it: copier tiers, Analyzer and dedicated environments.
 * Shared so every surface that describes a plan reads the same numbers.
 *
 * @param {object[]} subscriptions
 * @param {object | null} catalog
 */
export function summarizeHeldPlan(subscriptions, catalog) {
  // Seat-entitled (active/trialing) subs, matching selectCopierAccountLimit.
  const activeSubs = listSeatEntitledSubscriptions(subscriptions);
  const copierCounts = copierQuantitiesFromSubscriptions(activeSubs, null);
  const hasAnalyzer = subscriptionIncludesProduct(activeSubs, ADDON_PRODUCTS.analyzer, null);
  // Futures "unlimited" is a flat SKU (Stripe qty 1) — surface it as "(Unlimited)"
  // rather than "× 1", which misreads as a single futures account.
  const hasFuturesUnlimited = activeSubs.some((s) => {
    const nicks = [s.priceNickname, ...((s.lineItems ?? s.lineItemsList) || []).map((li) => li?.priceNickname)].filter(Boolean);
    return nicks.some((n) => {
      const m = parseNickname(n);
      return m?.product === PRODUCTS.futures && m?.variant === PRICE_VARIANTS.unlimited;
    });
  });

  // DE envs = SUM of DE line items across ALL active subs (each line = 1 env).
  // line.quantity = accounts inside that env. Line-level detection is
  // authoritative because bundle subs may have a generic productName at the
  // sub level while DE lives as one of their line items — checking
  // isDedicatedEnv(sub) first would miss those. Sub-level fallback only
  // applies when there are no line items at all (legacy solo-DE subs).
  let deEnvCount = 0;
  // Accounts are counted for CFD envs only — a futures env is flat/unlimited
  // and bills at Stripe quantity 1, which would read as "1 acct".
  let deAccountsTotal = 0;
  let deFuturesCount = 0;
  activeSubs.forEach((sub) => {
    const lines = dedicatedEnvLinesOf(sub, catalog);
    if (lines.length > 0) {
      deEnvCount += lines.length;
      lines.forEach((l) => {
        if (lineItemProduct(l, catalog, sub) === ADDON_PRODUCTS.dedicatedFutures) {
          deFuturesCount += 1;
          return;
        }
        deAccountsTotal += Math.max(1, Number(l.quantity) || 1);
      });
      return;
    }
    // Legacy solo DE sub — no DE line items, whole sub is one env.
    if (isDedicatedEnv(sub)) {
      deEnvCount += 1;
      deAccountsTotal += Math.max(1, Number(sub.quantity) || 1);
    }
  });

  const copierLabels = [];
  if (copierCounts.standard > 0) copierLabels.push(`Standard × ${copierCounts.standard}`);
  if (copierCounts.premium > 0) copierLabels.push(`Premium × ${copierCounts.premium}`);
  if (hasFuturesUnlimited) copierLabels.push('Futures (Unlimited)');
  else if (copierCounts.futures > 0) copierLabels.push(`Futures × ${copierCounts.futures}`);

  const planLabels = [...copierLabels];
  if (hasAnalyzer) planLabels.push('Analyzer');
  if (deEnvCount > 0) {
    // Use "envs" suffix only when there are multiple; otherwise "Dedicated Env"
    // reads more naturally for the single case.
    const deSingleLabel =
      deFuturesCount === deEnvCount
        ? 'Dedicated Env (Unlimited)'
        : `Dedicated Env (${deAccountsTotal} acct${deAccountsTotal === 1 ? '' : 's'})`;
    planLabels.push(deEnvCount === 1 ? deSingleLabel : `Dedicated Env × ${deEnvCount}`);
  }

  return { planLabels, copierLabels, copierCounts, hasAnalyzer, deEnvCount, deAccountsTotal };
}
